import axios from "axios";
import config from "../../../../config";
import * as actions from "../actions";

// Helper function to get auth headers
const getAuthHeaders = () => {
  return {
    headers: {
      "Authorization": `Bearer ${localStorage.getItem("accessToken")}`,
      "Content-Type": "application/json",
    }
  };
};

// ============= BRANDS HANDLERS =============

// Fetch all brands
export const fetchBrandsHandler = (params = { page: 1, size: 100 }) => {
  return async dispatch => {
    try {
      dispatch(actions.requestStart());
      const { data } = await axios.get(
        `${config.baseUrl}/brands?`,
        { ...getAuthHeaders(), params }
      );
      dispatch(actions.brandsFetchSuccess(data));
      return data;
    } catch (error) {
      dispatch(actions.requestFail(error));
      throw error;
    }
  };
};

// Get single brand detail
export const fetchBrandDetailHandler = (brandId) => {
  return async dispatch => {
    try {
      dispatch(actions.requestStart());
      const { data } = await axios.get(
        `${config.baseUrl}/brands/${brandId}`,
        getAuthHeaders()
      );
      dispatch(actions.brandDetailSuccess(data));
      return data;
    } catch (error) {
      dispatch(actions.requestFail(error));
      throw error;
    }
  };
};

// Create new brand
export const createBrandHandler = (brandData) => {
  return async dispatch => {
    try {
      dispatch(actions.requestStart());
      const { data } = await axios.post(
        `${config.baseUrl}/brands`,
        brandData,
        getAuthHeaders()
      );
      dispatch(actions.brandCreateSuccess(data));
      dispatch(actions.requestSuccess("Brand created successfully"));
      return data;
    } catch (error) {
      dispatch(actions.requestFail(error));
      throw error;
    }
  };
};

// Update brand
export const updateBrandHandler = (brandId, brandData) => {
  return async dispatch => {
    try {
      dispatch(actions.requestStart());
      const { data } = await axios.patch(
        `${config.baseUrl}/brands/${brandId}`,
        brandData,
        getAuthHeaders()
      );
      dispatch(actions.brandUpdateSuccess(data));
      dispatch(actions.requestSuccess("Brand updated successfully"));
      return data;
    } catch (error) {
      dispatch(actions.requestFail(error));
      throw error;
    }
  };
};

// ============= SUB BRANDS HANDLERS =============
// Note: Sub brands are nested within brands in the API

const SUB_BRANDS_BRAND_ID = "b009d6e8-b08e-42a2-90e5-7513626fccfc";

// Raw, non-dispatching fetch — used by the CSV exporter so the visible page
// state isn't replaced while we page through everything.
export const fetchSubBrandsPageRaw = async (params = { page: 1, size: 100 }) => {
  const { data } = await axios.get(
    `${config.baseUrl}/brands/${SUB_BRANDS_BRAND_ID}/sub-brands`,
    { ...getAuthHeaders(), params }
  );
  const items = data.items || data.data || data;
  const itemsWithBrand = Array.isArray(items)
    ? items.map((sb) => ({ ...sb, brand_id: sb.brand_id || SUB_BRANDS_BRAND_ID }))
    : items;
  return {
    items: itemsWithBrand || [],
    total: data.total ?? (Array.isArray(data) ? data.length : 0),
  };
};

// Fetch all sub brands across all brands (paginated)
export const fetchAllSubBrandsHandler = (params = { page: 1, size: 100 }) => {
  return async dispatch => {
    try {
      dispatch(actions.subBrandsLoadingStart());
      const { items, total } = await fetchSubBrandsPageRaw(params);
      const normalized = { data: items, total };
      dispatch(actions.subBrandsFetchSuccess(normalized));
      return normalized;
    } catch (error) {
      dispatch(actions.requestFail(error));
      throw error;
    }
  };
};

// Fetch all sub brands from a specific brand
export const fetchSubBrandsHandler = (brandId) => {
  return async dispatch => {
    try {
      dispatch(actions.subBrandsLoadingStart());
      const { data } = await axios.get(
        `${config.baseUrl}/brands/${brandId}`,
        getAuthHeaders()
      );
      dispatch(actions.subBrandsFetchSuccess(data.sub_brands || []));
      return data.sub_brands || [];
    } catch (error) {
      dispatch(actions.requestFail(error));
      throw error;
    }
  };
};

// Create new sub-brand under a brand
export const createSubBrandHandler = (brandId, subBrandData) => {
  return async dispatch => {
    try {
      dispatch(actions.requestStart());
      const { data } = await axios.post(
        `${config.baseUrl}/brands/${brandId}/sub-brands`,
        subBrandData,
        getAuthHeaders()
      );
      dispatch(actions.subBrandCreateSuccess(data));
      dispatch(actions.requestSuccess("Sub-brand created successfully"));
      return data;
    } catch (error) {
      dispatch(actions.requestFail(error));
      throw error;
    }
  };
};

// Update sub-brand
export const updateSubBrandHandler = (brandId, subBrandId, subBrandData) => {
  return async dispatch => {
    try {
      dispatch(actions.requestStart());
      const { data } = await axios.patch(
        `${config.baseUrl}/brands/${brandId}/sub-brands/${subBrandId}`,
        subBrandData,
        getAuthHeaders()
      );
      dispatch(actions.subBrandUpdateSuccess(data));
      dispatch(actions.requestSuccess("Sub-brand updated successfully"));
      return data;
    } catch (error) {
      dispatch(actions.requestFail(error));
      throw error;
    }
  };
};

// ============= SUB-BRAND VENDORS HANDLERS =============

// Fetch all vendors for a sub-brand
export const fetchSubBrandVendorsHandler = (brandId, subBrandId, params = { page: 1, size: 20 }) => {
  return async dispatch => {
    try {
      dispatch(actions.vendorsLoadingStart());
      const { data } = await axios.get(
        `${config.baseUrl}/brands/${brandId}/sub-brands/${subBrandId}/vendors`,
        { ...getAuthHeaders(), params }
      );
      dispatch(actions.vendorsFetchSuccess(data));
      return data;
    } catch (error) {
      dispatch(actions.requestFail(error));
      throw error;
    }
  };
};

// Get single vendor detail
export const fetchVendorDetailHandler = (brandId, subBrandId, vendorId) => {
  return async dispatch => {
    try {
      dispatch(actions.requestStart());
      const { data } = await axios.get(
        `${config.baseUrl}/brands/${brandId}/sub-brands/${subBrandId}/vendors/${vendorId}`,
        getAuthHeaders()
      );
      dispatch(actions.vendorDetailSuccess(data));
      return data;
    } catch (error) {
      dispatch(actions.requestFail(error));
      throw error;
    }
  };
};

// Create new vendor for a sub-brand
export const createSubBrandVendorHandler = (brandId, subBrandId, vendorData) => {
  return async dispatch => {
    try {
      dispatch(actions.requestStart());
      const { data } = await axios.post(
        `${config.baseUrl}/brands/${brandId}/sub-brands/${subBrandId}/vendors`,
        vendorData,
        getAuthHeaders()
      );
      dispatch(actions.vendorCreateSuccess(data));
      dispatch(actions.requestSuccess("Vendor created successfully"));
      return data;
    } catch (error) {
      dispatch(actions.requestFail(error));
      throw error;
    }
  };
};

// Update vendor
export const updateSubBrandVendorHandler = (brandId, subBrandId, vendorId, vendorData) => {
  return async dispatch => {
    try {
      dispatch(actions.requestStart());
      const { data } = await axios.patch(
        `${config.baseUrl}/brands/${brandId}/sub-brands/${subBrandId}/vendors/${vendorId}`,
        vendorData,
        getAuthHeaders()
      );
      dispatch(actions.vendorUpdateSuccess(data));
      dispatch(actions.requestSuccess("Vendor updated successfully"));
      return data;
    } catch (error) {
      dispatch(actions.requestFail(error));
      throw error;
    }
  };
};


// ============= CATEGORIES HANDLERS =============

// Fetch all categories
export const fetchCategoriesHandler = (params = { page: 1, size: 100 }) => {
  return async dispatch => {
    try {
      dispatch(actions.categoriesLoadingStart());
      const { data } = await axios.get(
        `${config.baseUrl}/categories`,
        { ...getAuthHeaders(), params }
      );
      dispatch(actions.categoriesFetchSuccess(data));
      return data;
    } catch (error) {
      dispatch(actions.requestFail(error));
      throw error;
    }
  };
};

// Get single category detail
export const fetchCategoryDetailHandler = (categoryId) => {
  return async dispatch => {
    try {
      dispatch(actions.requestStart());
      const { data } = await axios.get(
        `${config.baseUrl}/categories/${categoryId}`,
        getAuthHeaders()
      );
      dispatch(actions.categoryDetailSuccess(data));
      return data;
    } catch (error) {
      dispatch(actions.requestFail(error));
      throw error;
    }
  };
};

// Create new category
export const createCategoryHandler = (categoryData) => {
  return async dispatch => {
    try {
      dispatch(actions.requestStart());
      const { data } = await axios.post(
        `${config.baseUrl}/categories`,
        categoryData,
        getAuthHeaders()
      );
      dispatch(actions.categoryCreateSuccess(data));
      dispatch(actions.requestSuccess("Category created successfully"));
      return data;
    } catch (error) {
      dispatch(actions.requestFail(error));
      throw error;
    }
  };
};

// Update category
export const updateCategoryHandler = (categoryId, categoryData) => {
  return async dispatch => {
    try {
      dispatch(actions.requestStart());
      const { data } = await axios.patch(
        `${config.baseUrl}/categories/${categoryId}`,
        categoryData,
        getAuthHeaders()
      );
      dispatch(actions.categoryUpdateSuccess(data));
      dispatch(actions.requestSuccess("Category updated successfully"));
      return data;
    } catch (error) {
      dispatch(actions.requestFail(error));
      throw error;
    }
  };
};

// ============= COMMON HANDLERS =============

// Clear error
export const clearErrorHandler = () => {
  return dispatch => {
    dispatch(actions.clearError());
  };
};

// Clear success message
export const clearSuccessHandler = () => {
  return dispatch => {
    dispatch(actions.clearSuccess());
  };
};

// Wallet Vendor handlers
export const fetchVendorDetailsHandler = (vendor) => {
  return async dispatch => {
    try {
      dispatch(actions.requestStart());
      const { data } = await axios.get(
        `${config.baseUrl}/orders/vendor-details`,
        { 
          ...getAuthHeaders(),
          params: { vendor }
        }
      );
      dispatch(actions.vendorDetailsFetchSuccess(data));
      return data;
    } catch (error) {
      dispatch(actions.requestFail(error));
      throw error;
    }
  };
};

export const updateVendorDetailsHandler = (walletData) => {
  return async dispatch => {
    try {
      dispatch(actions.requestStart());
      const { data } = await axios.patch(
        `${config.baseUrl}/orders/vendor-details`,
        walletData,
        getAuthHeaders()
      );
      dispatch(actions.vendorDetailsUpdateSuccess(data));
      dispatch(actions.requestSuccess("Wallet balance updated successfully"));
      return data;
    } catch (error) {
      dispatch(actions.requestFail(error));
      throw error;
    }
  };
};

// ============= ORDERS HANDLERS =============

// Fetch orders with search
export const fetchOrdersByMobileHandler = (searchQuery, params = { page: 1, size: 20 }) => {
  return async dispatch => {
    try {
      dispatch(actions.ordersLoadingStart());
      const queryParams = {
        ...params
      };
      // Add search parameter if provided
      if (searchQuery && searchQuery.trim()) {
        queryParams.search = searchQuery.trim();
      }
      const { data } = await axios.get(
        `${config.baseUrl}/orders`,
        { ...getAuthHeaders(), params: queryParams }
      );
      console.log('Orders API response:', data); // Debug log
      dispatch(actions.ordersFetchSuccess(data, searchQuery));
      return data;
    } catch (error) {
      dispatch(actions.requestFail(error));
      throw error;
    }
  };
};

// Get single order detail
export const fetchOrderDetailHandler = (orderId) => {
  return async dispatch => {
    try {
      dispatch(actions.ordersLoadingStart());
      const { data } = await axios.get(
        `${config.baseUrl}/orders/${orderId}`,
        getAuthHeaders()
      );
      dispatch(actions.orderDetailSuccess(data));
      return data;
    } catch (error) {
      dispatch(actions.requestFail(error));
      throw error;
    }
  };
};

// Clear orders
export const clearOrdersHandler = () => {
  return dispatch => {
    dispatch(actions.ordersClear());
  };
};

// Cancel order
export const cancelOrderHandler = (orderId) => {
  return async dispatch => {
    try {
      dispatch(actions.requestStart());
      const { data } = await axios.patch(
        `${config.baseUrl}/orders/${orderId}/cancel`,
        {},
        getAuthHeaders()
      );
      dispatch(actions.orderUpdateSuccess(data));
      dispatch(actions.requestSuccess("Order cancelled successfully"));
      return data;
    } catch (error) {
      dispatch(actions.requestFail(error));
      throw error;
    }
  };
};

// Refund order
export const refundOrderHandler = (orderId, refundType) => {
  return async dispatch => {
    try {
      dispatch(actions.requestStart());
      const { data } = await axios.patch(
        `${config.baseUrl}/orders/${orderId}/refund`,
        { refund_type: refundType },
        getAuthHeaders()
      );
      dispatch(actions.orderUpdateSuccess(data));
      dispatch(actions.requestSuccess(`Order refunded to ${refundType} successfully`));
      return data;
    } catch (error) {
      dispatch(actions.requestFail(error));
      throw error;
    }
  };
};

// ============= TRANSACTIONS HANDLERS =============

// Fetch transactions with filters and enhanced search
export const fetchTransactionsHandler = (params = { page: 1, size: 50 }) => {
  return async dispatch => {
    try {
      dispatch(actions.transactionsLoadingStart());
      
      const { data } = await axios.get(
        `${config.baseUrl}/transactions`,
        { ...getAuthHeaders(), params }
      );
      dispatch(actions.transactionsFetchSuccess(data));
      return data;
    } catch (error) {
      dispatch(actions.requestFail(error));
      throw error;
    }
  };
};

// Clear transactions
export const clearTransactionsHandler = () => {
  return dispatch => {
    dispatch(actions.transactionsClear());
  };
};

// Refund transaction
export const refundTransactionHandler = (transactionId) => {
  return async dispatch => {
    try {
      dispatch(actions.requestStart());
      const { data } = await axios.post(
        `${config.baseUrl}/transactions/${transactionId}/refund`,
        {},
        getAuthHeaders()
      );
      
      if (data.success) {
        dispatch(actions.requestSuccess(data.message));
      } else {
        dispatch(actions.requestFail({ response: { data: { message: data.message } } }));
      }
      
      return data;
    } catch (error) {
      dispatch(actions.requestFail(error));
      throw error;
    }
  };
};